import { differenceInDays, differenceInWeeks, endOfDay, fromUnixTime, startOfDay } from 'date-fns';
import { FocusTypes, type ForecastActivityState, ForecastType, type Signature, SpecificityTypes } from './constants';

const extractForecastType = (state: string[]) => {
  if (state.includes('rest')) {
    return ForecastType.Rest;
  } else if (state.includes('recovery')) {
    return ForecastType.Recovery;
  } else if (state.includes('high_intensity')) {
    return ForecastType.HighIntensity;
  } else if (state.includes('low_intensity')) {
    return ForecastType.LowIntensity;
  } else if (state.includes('event_A')) {
    return ForecastType.Event;
  } else {
    console.warn(state);
    throw new Error('Unknown forecast state');
  }
};

// The state the Training Explanation (ForecastDescription) is written from, for a forecast activity of the current
// training plan that has not been completed. Undefined for anything else.
export function getForecastActivityState(
  activity: any,
  trainingPlan: any,
  currentSignature: Signature | undefined,
  lastBTDate: Date | null,
): ForecastActivityState | undefined {
  if (
    !trainingPlan?.forecastResults?.days || !activity ||
    !activity.state?.includes('forecast') || activity.state?.includes('completed') ||
    activity.training_plan_id !== (trainingPlan.training_plan_id ?? trainingPlan._id)
  ) {
    return undefined;
  }

  const days = trainingPlan.forecastResults.days;

  if ((!activity.forecast_index && activity.forecast_index !== 0) || activity.forecast_index >= days.length) {
    console.warn(activity, days);
    return undefined;
  }

  const todayState = extractForecastType(days.at(activity.forecast_index).state);

  let tommorowState: ForecastType | null = null;
  if (activity.forecast_index + 1 < days.length) {
    const tommorowDay = days.at(activity.forecast_index + 1);
    tommorowState = extractForecastType(tommorowDay.state);
  }

  let specificity: SpecificityTypes | null = null;
  switch ((activity.spr ?? activity.specRating)?.toLowerCase()) {
    case 'pure':
      specificity = SpecificityTypes.Pure;
      break;
    case 'mixed':
      specificity = SpecificityTypes.Mixed;
      break;
    case 'polar':
      specificity = SpecificityTypes.Polar;
      break;
    default:
      console.warn('Unknown specificity. Using Pure as default', activity);
      specificity = SpecificityTypes.Pure;
  }

  let focus: FocusTypes | null = null;
  switch ((activity.focus ?? activity.fa)?.toLowerCase()) {
    case 'road sprinter':
      focus = FocusTypes.RoadSprinter;
      break;
    case 'pursuiter':
      focus = FocusTypes.Pursuiter;
      break;
    case 'puncheur':
      focus = FocusTypes.Puncheur;
      break;
    case 'breakaway specialist':
      focus = FocusTypes.BreakawaySpecialist;
      break;
    case 'rouleur':
      focus = FocusTypes.Rouleur;
      break;
    case 'gc specialist':
      focus = FocusTypes.GcSpecialist;
      break;
    case 'climber':
      focus = FocusTypes.Climber;
      break;
    case 'endurance':
      focus = FocusTypes.Endurance;
      break;
    default:
      console.warn(activity, 'Unknown focus. Using endurance as default');
      focus = FocusTypes.Endurance;
      break;
  }

  let daysToHighIntensity: number = -1;
  let weeksToEvent: number = -1;
  for (let i = activity.forecast_index + 1; i < days.length; i++) {
    if (daysToHighIntensity < 0 && days[i].state?.includes('high_intensity')) {
      const nextHIDate = fromUnixTime(days[i].t);
      daysToHighIntensity = differenceInDays(nextHIDate, startOfDay(new Date(activity.start_date)));
    }
    if (weeksToEvent < 0 && days[i].state?.includes('event_A')) {
      const nextEventDate = fromUnixTime(days[i].t);
      weeksToEvent = differenceInWeeks(nextEventDate, startOfDay(new Date(activity.start_date)));
    }
  }

  if (trainingPlan.selected_mode == 'target_event') {
    weeksToEvent = 9999;
  }

  const daysSinceBT = lastBTDate ? differenceInDays(endOfDay(new Date(activity.start_date)), lastBTDate) : 99999;
  const polarizationLevel = trainingPlan.settings?.polarization ?? 4;

  return {
    type: todayState,
    nextDayType: tommorowState,
    specificity,
    focus,
    daysToHighIntensity,
    weeksToEvent,
    daysSinceBT,
    polarizationLevel,
    isChallenging: true,
    currentSignature,
    activitySignature: activity.signature,
    duration: activity.duration,
    targets: { xss: activity.xss, xlss: activity.xlss, xhss: activity.xhss, xpss: activity.xpss },
    path: activity.path,
  };
}
